import React, { useState, useEffect, useMemo } from "react";
import { 
  Typography, 
  Box, 
  Paper, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  SelectChangeEvent
} from "@mui/material";
import { useSchemes } from "../../../contexts/SchemesContext";
import { OffensiveGamePlan, GameplanPlay } from "../../../types/SchemesTypes";
import { useLogger } from "../../../contexts/LoggerContext";

// TODO: capture the start of the drive (the first play should be denoted)
interface PersonnelGroup {
  id: number;
  name: string;
}

interface Formation {
  id: number;
  name: string;
}

interface CodeWord {
  id: number;
  code_word: string;
  name: string;
  called_pass: boolean;
}

export const OffenseGameLogger = () => {
  const { offensiveGamePlans } = useSchemes();
  const { selectedGame } = useLogger();
  const [gamePlan, setGamePlan] = useState<OffensiveGamePlan | null>(null);
  const [personnelGroups, setPersonnelGroups] = useState<PersonnelGroup[]>([]);
  const [formations, setFormations] = useState<Formation[]>([]);
  
  const [selectedPersonnel, setSelectedPersonnel] = useState<number | "">("");
  const [selectedFormation, setSelectedFormation] = useState<number | "">("");
  const [formationDirection, setFormationDirection] = useState<"Left" | "Right">("Right");
  const [selectedCodeWord, setSelectedCodeWord] = useState<number | "">("");

  // Find the game plan for this game and extract unique personnel/formations
  useEffect(() => {
    const currentGamePlan = offensiveGamePlans.find(gp => gp.game_details.id === selectedGame?.id);
    if (currentGamePlan) {
      setGamePlan(currentGamePlan);
      
      // Extract unique personnel groups and formations from plays
      const uniquePersonnel = new Map<number, string>();
      const uniqueFormations = new Map<number, string>();
      
      currentGamePlan.plays.forEach(play => {
        if (play.play_details?.personnel_group) {
          uniquePersonnel.set(play.play_details.personnel_group, play.play_details.personnel_group_name);
        }
        if (play.play_details?.formation) {
          uniqueFormations.set(play.play_details.formation, play.play_details.formation_name);
        }
      });
      
      // Convert Maps to arrays of objects with id and name
      setPersonnelGroups(Array.from(uniquePersonnel.entries()).map(([id, name]) => ({
        id,
        name
      })));
      setFormations(Array.from(uniqueFormations.entries()).map(([id, name]) => ({
        id,
        name
      })));
    }
  }, [selectedGame, offensiveGamePlans]);

  console.log(gamePlan);

  // Filter available code words based on selections
  const availableCodeWords = useMemo(() => {
    if (!gamePlan || !selectedPersonnel || !selectedFormation) return [];

    return gamePlan.plays
      .filter(play => {
        const matchesPersonnel = play.play_details?.personnel_group === selectedPersonnel;
        const matchesFormation = play.play_details?.formation === selectedFormation;
        const matchesDirection = play.play_details?.formation_direction === formationDirection;
        return matchesPersonnel && matchesFormation && matchesDirection;
      })
      .map((play, index) => ({
        id: index,
        code_word: play.play_details?.code_word || "",
        name: play.play_details?.name || "",
        called_pass: play.play_details?.called_pass || false
      }));
  }, [gamePlan, selectedPersonnel, selectedFormation, formationDirection]);

  const handlePersonnelChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedPersonnel(event.target.value as number);
    setSelectedCodeWord(""); // Reset code word when personnel changes
  };

  const handleFormationChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedFormation(event.target.value as number);
    setSelectedCodeWord(""); // Reset code word when formation changes
  };

  const handleDirectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormationDirection(event.target.value as "Left" | "Right");
    setSelectedCodeWord(""); // Reset code word when direction changes
  };

  const handleCodeWordChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedCodeWord(event.target.value as number);
  };

  return (
    <Paper elevation={3} sx={{ p: 5, mt: 3 }}>
      <Box>
        
        {!gamePlan ? (
          <Typography color="error">No game plan found for this game</Typography>
        ) : (
          <Grid container spacing={5} sx={{ mt: 2 }}>
            {/* Personnel Group Selection */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth sx={{ mb: 2, minWidth: 200 }}>
                <InputLabel id="personnel-group-label">Personnel Group</InputLabel>
                <Select
                  labelId="personnel-group-label"
                  id="personnel-group"
                  value={selectedPersonnel}
                  label="Personnel Group"
                  onChange={handlePersonnelChange}
                >
                  {personnelGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Formation Selection */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth sx={{ mb: 2, minWidth: 200 }}>
                <InputLabel id="formation-label">Formation</InputLabel>
                <Select
                  labelId="formation-label"
                  id="formation"
                  value={selectedFormation}
                  label="Formation"
                  onChange={handleFormationChange}
                >
                  {formations.map((formation) => (
                    <MenuItem key={formation.id} value={formation.id}>
                      {formation.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Formation Direction */}
            <Grid item xs={12} md={3}>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  name="formation-direction"
                  value={formationDirection}
                  onChange={handleDirectionChange}
                >
                  <FormControlLabel value="Left" control={<Radio />} label="Left" />
                  <FormControlLabel value="Right" control={<Radio />} label="Right" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Code Word Selection */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth sx={{ mb: 2, minWidth: 200 }}>
                <InputLabel id="code-word-label">Code Word</InputLabel>
                <Select
                  labelId="code-word-label"
                  id="code-word"
                  value={selectedCodeWord}
                  label="Code Word"
                  onChange={handleCodeWordChange}
                  disabled={!selectedPersonnel || !selectedFormation}
                >
                  {availableCodeWords.map((codeWord) => (
                    <MenuItem key={codeWord.id} value={codeWord.id}>
                      {codeWord.code_word} ({codeWord.called_pass ? "Pass" : "Run"})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Box>
    </Paper>
  );
};

export default OffenseGameLogger;
